import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useConnectionSetupOptions } from "@/lib/api/connections";

/** Only a server-verified local install URL may start or repair an install. */
export function appInstallUrl(url: string, { recovery = false, installShape }: { recovery?: boolean; installShape?: string | null } = {}): string {
  if (!recovery || installShape === "oauth-code-exchange") return url;
  const parsed = new URL(url);
  parsed.searchParams.set("recovery", "1");
  return parsed.toString();
}

/**
 * Connect / Reconnect action for a connector whose auth is an App installation.
 * Renders an anchor (not router navigation) so the browser leaves the SPA and
 * hits the gateway start route with cookies, which is what mints the signed
 * state and redirects to the provider.
 */
export function AppInstallButton({
  organizationId,
  connectorKey,
	providerLabel,
	recovery = false,
	installShape,
	size = "sm",
	variant,
	className,
	label,
	icon,
}: {
  organizationId: string;
  connectorKey: string;
	/** The connector's display name; without one the copy stays generic ("app"), never a guessed name from the key. */
	providerLabel?: string;
	recovery?: boolean;
	installShape?: string | null;
	size?: "sm" | "default";
	variant?: "default" | "outline";
	className?: string;
	label?: string;
	icon?: ReactNode;
}) {
  const query = useConnectionSetupOptions(organizationId, connectorKey);
  const local = query.data?.options.find(option => option.kind === 'local');
  if (!organizationId || query.isPending) return <span className="text-sm text-muted-foreground">Checking app setup…</span>;
  if (query.isError) return <div className="space-y-1"><p className="text-sm text-muted-foreground">App setup could not be checked.</p><Button variant="link" className="px-0" onClick={() => void query.refetch()}>Retry app setup</Button></div>;
  if (!local?.configured || !local.url) return <p className="text-sm text-muted-foreground">This server has no app configured. Choose an available connection option or configure your own app.</p>;
	const name = providerLabel ?? "app";
	const text = label ?? `${recovery ? "Reconnect" : "Connect"} ${name}`;
	return (
		<Button asChild size={size} variant={variant} className={className}>
			<a href={appInstallUrl(local.url, { recovery, installShape })}>
				{icon}
				{text}
			</a>
		</Button>
	);
}
